import {
  Box,
  Button,
  Icon,
  Text,
  Flex,
  TextArea,
  Popover,
} from "orcs-design-system";
import React, { useRef } from "react";
import { useAISidebar } from "src/hooks/useAISidebar";
import { AIChat } from "./AIChat";
import { AILoader } from "./AILoader";
import { AIWarning } from "./AIWarning";
import { useAIChat } from "./context/AIChatContext";
import { AIChatProvider } from "./context/AIChatProvider";
import { SOURCE_TYPES } from "./consts";

const AIChatWindow = () => {
  const {
    question,
    setQuestion,
    onSessionClear,
    complete,
    onSendQuestion,
    showLoader,
  } = useAIChat();
  const inputBoxRef = /** @type {typeof useRef<HTMLInputElement>} */ (useRef)();

  const { clearAI, isOpen } = useAISidebar();

  const closeAndClearChat = React.useCallback(() => {
    // Clear the session cookie if the function is available
    if (onSessionClear) {
      onSessionClear();
    }

    // Clear localStorage and close the chat
    clearAI();
  }, [onSessionClear, clearAI]);

  const onSubmit = React.useCallback(
    (e) => {
      e.preventDefault();
      inputBoxRef.current.value = "";
      onSendQuestion();
    },
    [onSendQuestion]
  );

  React.useEffect(() => {
    if (inputBoxRef.current && isOpen) {
      inputBoxRef.current.focus();
      inputBoxRef.current.select();
    }
  }, [inputBoxRef, complete, isOpen]);

  return (
    // @ts-ignore
    <Box overflow="hidden" height="100%" bg="white">
      {/* @ts-ignore */}
      <Box
        width={["100%", "280px", "300px", "350px", "360px", "380px"]}
        height="inherit"
        background="white"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        borderLeft="1px solid"
        borderLeftColor="greyLighter"
      >
        {/* Header */}
        {/* @ts-ignore */}
        <Flex
          alignItems="center"
          justifyContent="space-between"
          padding="r"
          borderColor="greyLight"
          background="white"
          flexShrink={0}
        >
          {/* @ts-ignore */}
          <Flex alignItems="center" gap="s">
            {/* @ts-ignore */}
            <Box
              width="24px"
              height="24px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius={2}
              color="white"
              bg="greyLighter"
            >
              {/* @ts-ignore */}
              <Icon icon={["fas", "comment"]} color="white" />
            </Box>
            {/* @ts-ignore */}
            <Text fontWeight="600" fontSize="1">
              Ask TeamForm
            </Text>
          </Flex>
          {/* @ts-ignore */}
          <Popover
            text="Close chat and clear chat history"
            direction="bottom"
            width="fit-content"
          >
            {/* @ts-ignore */}
            <Button
              variant="ghost"
              iconOnly
              bg="greyLighter"
              small
              height="22px"
              width="22px"
              onClick={closeAndClearChat}
              ariaLabel="Close chat and clear chat history"
            >
              {/* @ts-ignore */}
              <Icon icon={["fas", "times"]} color="greyDarker" />
            </Button>
          </Popover>
        </Flex>

        {/* Chat Content */}
        {/* @ts-ignore */}
        <Box flex={1} minHeight={0}>
          <AIChat />
        </Box>

        <AILoader loading={showLoader} />

        {/* Input Area */}
        {/* @ts-ignore */}
        <Box
          padding="r"
          borderColor="greyLight"
          background="white"
          flexShrink={0}
          width="80%"
        >
          <AIWarning justifyContent="flex-start" mb="s" />

          <form onSubmit={onSubmit}>
            {/* @ts-ignore */}
            <Flex alignItems="center" gap="s">
              <TextArea
                /* @ts-ignore */
                value={question}
                /* @ts-ignore */
                onChange={(e) => setQuestion(e.target.value)}
                /* @ts-ignore */
                disabled={showLoader}
                name="question"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    e.target.form.requestSubmit();
                  }
                }}
                resize="none"
                fullWidth
                ref={inputBoxRef}
              />
              {/* @ts-ignore */}
              <Button
                type="submit"
                iconOnly
                variant="dangerAlternate"
                ariaLabel="Send message"
                borderRadius="6px"
                width={28}
                height={28}
                display="flex"
                style={{
                  right: 8,
                  top: "50%",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                  width="20"
                  height="20"
                >
                  <defs>
                    <linearGradient
                      id="fa-custom-gradient"
                      x1="0%"
                      y1="0%"
                      x2="0%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#0091EA" />
                      <stop offset="100%" stopColor="#9B51E0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M313.6 119.7L209.6 7.7c-9.5-10.2-25.7-10.2-35.2 0L70.4 119.7C56.2 135 67.1 160 88 160H152v272H68a12 12 0 0 0 -8.5 3.5l-56 56C-4 499.1 1.3 512 12 512H208c13.3 0 24-10.7 24-24V160h64c20.9 0 31.9-25 17.6-40.3z"
                    fill="url(#fa-custom-gradient)"
                  />
                </svg>
              </Button>
            </Flex>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

const ProvidedAIChatWindow = () => {
  return (
    <AIChatProvider sourceId={SOURCE_TYPES.chat_q_and_a} subjectId="custom">
      <AIChatWindow />
    </AIChatProvider>
  );
};

export default ProvidedAIChatWindow;
